// Phase 1C -- Decisions: every open thread, "needs you" first. A list a
// founder can scan in seconds (Linear-style rows), not a card grid.
import { useNavigate } from 'react-router-dom';
import { CaretRight } from '@phosphor-icons/react';
import { useFounderWorkspace, useFounderPath } from '../FounderWorkspace';
import {
  formatCheckFailureReason,
  formatMeasurementState,
  latestCheckAttention,
  latestCheckBlocksAllClear,
} from '@/lib/somHistoryHelpers';
import { Chip, EmptyState, Loading, PageHeader, STATUS_COPY, formatDate } from '../ui';

export default function DecisionsPage() {
  const { summary, workspaceId, loadingClients } = useFounderWorkspace();
  const fp = useFounderPath();
  const navigate = useNavigate();
  if (loadingClients || (workspaceId && !summary)) return <Loading />;
  const decisions = summary?.decisions || [];
  const needsYou = summary?.needs_you || 0;
  const attention = latestCheckAttention(summary);
  const blocksAllClear = latestCheckBlocksAllClear(attention);
  const title = blocksAllClear
    ? (attention.kind === 'incomplete'
      ? 'The latest check did not have enough answers'
      : 'The latest check could not be completed')
    : (needsYou ? `${needsYou} decision${needsYou === 1 ? '' : 's'} need${needsYou === 1 ? 's' : ''} you` : 'Nothing needs you right now');
  const emptyBody = blocksAllClear
    ? `${attention.check?.created_at ? `Latest check ${formatDate(attention.check.created_at, true)}. ` : ''}${formatMeasurementState(attention.check)}. ${formatCheckFailureReason(attention.check)} That is not the same as finding nothing worth acting on.`
    : "Martian opens a decision when its checks find something worth acting on. Nothing has been found yet — that's an honest result, not an error.";

  return (
    <div data-testid="decisions-page">
      <PageHeader
        eyebrow="Decisions"
        title={title}
        summary="Each decision is one problem Martian found, what it recommends, the work it prepared and how it will measure the result."
      />
      {decisions.length === 0 ? (
        <EmptyState title="No decisions yet" body={emptyBody} testId={blocksAllClear ? 'decisions-failed-check' : undefined} />
      ) : (
        <ul className="divide-y divide-[var(--border)] border-y border-[var(--border)]">
          {decisions.map((d) => {
            const status = STATUS_COPY[d.status] || STATUS_COPY.in_progress;
            return (
              <li key={d.id}>
                <button type="button" onClick={() => navigate(fp(`/decisions/${encodeURIComponent(d.id)}`))} data-testid="decision-row"
                  className="w-full flex items-start gap-4 py-5 text-left group focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)] rounded-sm">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Chip tone={status.tone}>{status.label}</Chip>
                      <span className="text-[12.5px] text-[var(--text-tertiary)]">{d.channel}{d.observed_at ? ` · ${formatDate(d.observed_at)}` : ''}</span>
                    </div>
                    <p className="text-[16px] font-semibold leading-snug group-hover:text-[var(--accent)] text-balance">{d.found}</p>
                    {d.recommendation && <p className="mt-1 text-[14px] text-[var(--text-secondary)]">Martian recommends: {d.recommendation}</p>}
                  </div>
                  <CaretRight size={16} className="mt-2 text-[var(--text-tertiary)] shrink-0" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
